import moment from 'moment';
import {getLocalTimezoneLabel} from '../../../../utils/datetime';

const localDateTime = (value) => `${moment(value).format('lll')} ${getLocalTimezoneLabel(value)}`;

// Normalize manager object to a common shape used across the UI
export function normalizeManager(m = {}) {
  return {
    // `username` is the pseudonym shown to non-superusers, who no longer
    // receive peer email/name (PII invariant). Keep it ahead of the raw
    // `user` UUID so peer managers render readably. See audit A260610-API-H1.
    manager_name: m.manager_name || m.user_name || m.name || m.username || m.user || '',
    user_email: m.user_email || m.email || '',
    role: m.role || m.role_name || m.permission || '',
  };
}

export function formatRoleLabel(manager) {
  /* Remove underscores and capitalize the first letter of each word (e.g. manager_name => Manager Name)*/
  if (!manager) return '';
  const raw = typeof manager === 'string' ? manager : (manager.role || 'manager');
  return String(raw).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function metaRows(r) {
  if (!r) return [];
  const rows = [];
  rows.push(['ID', r.id || 'N/A']);
  if (r.email) rows.push(['Email', r.email]);
  rows.push(['Name', r.name || 'N/A']);
  rows.push(['Use case', r.use_case || 'N/A']);
  rows.push(['Timezone', r.timezone || 'N/A']);
  if (r.budget) rows.push(['Budget', `€${parseFloat(r.budget).toLocaleString()}`]);
  if (r.metadata && typeof r.metadata === 'object') {
    Object.keys(r.metadata).slice(0, 10).forEach(k => rows.push([k, String(r.metadata[k])]));
  }
  rows.push(['Registered', r.registered_at ? localDateTime(r.registered_at) : 'N/A']);
  rows.push(['Created', r.created_at ? localDateTime(r.created_at) : 'N/A']);
  rows.push(['Updated', r.updated_at ? moment(r.updated_at).fromNow() : 'N/A']);
  return rows;
}

// Determine if a resource is managed by the user (helper used by container)
export function isMine(r, userDetails, resourceManagerDetails = []) {
  if (!userDetails) return false;
  const uid = userDetails.id || userDetails.user_id || userDetails.uuid;
  const email = userDetails.email || userDetails.username;

  // owner
  const isOwner = !!(
    (uid && (r.user === uid || (r.owner && r.owner.id === uid))) ||
    (email && (r.email === email || (r.owner && r.owner.email === email)))
  );

  let isManagerInline = false;
  if (Array.isArray(r.managers) && r.managers.length > 0) {
    isManagerInline = r.managers.some(m => (m.user && uid && String(m.user) === String(uid)) || (m.email && email && String(m.email).toLowerCase() === String(email).toLowerCase()));
  }

  const isManagerExternal = (resourceManagerDetails || []).some(
    item => item.resource === r.id || item.resource_name === r.name
  );

  return isOwner || isManagerInline || isManagerExternal;
}

// Lookup a single manager entry for a resource (prefers inline managers)
export function getManagerEntry(r, userDetails, resourceManagerDetails = []) {
  if (!r) return null;
  if (Array.isArray(r.managers) && r.managers.length > 0) {
    if (userDetails) {
      const uid = userDetails.id || userDetails.user_id || userDetails.uuid;
      const email = userDetails.email || userDetails.username;
      const found = r.managers.find(m => (m.user && uid && String(m.user) === String(uid)) || (m.email && email && String(m.email).toLowerCase() === String(email).toLowerCase()));
      if (found) return normalizeManager(found);
    }
    return normalizeManager(r.managers[0]);
  }

  if (!resourceManagerDetails || resourceManagerDetails.length === 0) return null;
  const targetId = r?.id !== undefined && r?.id !== null ? String(r.id) : null;
  const targetName = (r?.name || '').toString().trim().toLowerCase();
  const byId = new Map();
  const byName = new Map();
  resourceManagerDetails.forEach(item => {
    const possibleIds = [item?.resource, item?.resource_id, item?.id, item?.resource_pk];
    possibleIds.forEach(pid => { if (pid !== undefined && pid !== null) byId.set(String(pid), item); });
    const name = (item?.resource_name || item?.name || '').toString().trim().toLowerCase();
    if (name) byName.set(name, item);
  });
  if (targetId && byId.has(targetId)) return normalizeManager(byId.get(targetId));
  if (targetName && byName.has(targetName)) return normalizeManager(byName.get(targetName));

  const found = resourceManagerDetails.find(item => {
    const itemResource = item?.resource !== undefined && item?.resource !== null ? String(item.resource) : null;
    if (itemResource && targetId && itemResource === targetId) return true;
    const itemName = (item?.resource_name || item?.name || '').toString().trim().toLowerCase();
    if (itemName && targetName) return itemName === targetName;
    return false;
  });
  return found ? normalizeManager(found) : null;
}

export function getManagerEntries(r, resourceManagerDetails = []) {
  if (!r) return [];
  if (Array.isArray(r.managers) && r.managers.length > 0) {
    return r.managers.map(normalizeManager);
  }
  const targetId = r?.id !== undefined && r?.id !== null ? String(r.id) : null;
  const targetName = (r?.name || '').toString().trim().toLowerCase();
  return (resourceManagerDetails || []).filter(item => {
    const itemResource = item?.resource !== undefined && item?.resource !== null ? String(item.resource) : null;
    if (itemResource && targetId && itemResource === targetId) return true;
    const itemName = (item?.resource_name || item?.name || '').toString().trim().toLowerCase();
    if (itemName && targetName) return itemName === targetName;
    return false;
  }).map(normalizeManager);
}

